import { Avatar, Button, Icons, Input } from "@jod/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type ChangeEvent, useRef, useState } from "react";
import { customerBackendApi } from "../features/api/backend.ts";
import { useT } from "../features/i18n/index.ts";
import { useLiff } from "../features/liff/use-liff.ts";
import { nicknameGlyphs } from "../mocks/data.ts";

export const Route = createFileRoute("/line-profile")({
  component: LineProfileScreen,
});

function LineProfileScreen() {
  const t = useT();
  const navigate = useNavigate();
  const { profile } = useLiff();
  const [name, setName] = useState(profile?.displayName ?? "");
  const [saving, setSaving] = useState(false);
  const backendEnabled = customerBackendApi !== null;

  // Avatar source priority: uploaded photo → LINE photo → chosen glyph → initial.
  // A glyph is the privacy-friendly "no face" alternative; picking one or the
  // other clears the conflicting choice.
  const linePhoto = profile?.pictureUrl;
  const [customPhoto, setCustomPhoto] = useState<string | null>(null);
  const [glyph, setGlyph] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const imageUrl = customPhoto ?? (glyph ? undefined : linePhoto);
  const shownGlyph = customPhoto ? undefined : (glyph ?? undefined);
  const usingCustom = customPhoto !== null || glyph !== null;

  // NOTE: mock-only. Persisting this photo as a reusable cross-visit social
  // profile needs a backend avatar-upload endpoint + a new ADR superseding
  // ADR-0011 (which currently keeps patron avatars transient/never-stored).
  const pickPhoto = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCustomPhoto((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setGlyph(null);
    e.target.value = "";
  };

  const useLinePhoto = () => {
    setCustomPhoto((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setGlyph(null);
  };

  // Persist display name + avatar choice (FR-CUS-025) before continuing. A
  // custom uploaded photo still cannot be persisted (no object-storage
  // endpoint yet — same NOTE as `pickPhoto` above), so it falls back to
  // `avatarType: "line"` server-side; the upload only affects this session's
  // local preview. Persistence failure never blocks onboarding — the
  // customer can still finish their venue night and retry from /profile.
  const continueToJoin = async () => {
    if (backendEnabled && customerBackendApi) {
      setSaving(true);
      try {
        const current = await customerBackendApi.getMyProfile();
        await customerBackendApi.updateMyProfile({
          displayName: name.trim() || current.displayName,
          avatarType: glyph ? "glyph" : "line",
          avatarGlyph: glyph,
          statusLine: current.statusLine ?? null,
          bio: current.bio ?? null,
          vibes: current.vibes,
          instagramHandle: current.instagramHandle ?? null,
          showVibes: current.showVibes,
          showBio: current.showBio,
        });
      } catch {
        // Non-blocking — see comment above.
      } finally {
        setSaving(false);
      }
    }
    navigate({ to: "/join-table" });
  };

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-6 px-7 py-10">
      <div className="text-center">
        <div className="jod-kicker">{t("common.profileTonight")}</div>
      </div>

      <div className="flex flex-col items-center gap-4">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="relative rounded-full"
          aria-label={t("lineProfile.changePhotoAria")}
        >
          <Avatar
            imageUrl={imageUrl}
            glyph={shownGlyph}
            label={name || t("common.you")}
            size={96}
            ring
          />
          <span className="absolute right-0 bottom-0 grid size-8 place-items-center rounded-full border-2 border-[var(--background)] bg-gold text-night">
            <Icons.IconPlus size={16} />
          </span>
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickPhoto} />

        <div className="flex items-center gap-2">
          <Button variant="quiet" size="sm" onClick={() => fileRef.current?.click()}>
            {t("lineProfile.changePhoto")}
          </Button>
          {usingCustom ? (
            <Button variant="quiet" size="sm" onClick={useLinePhoto}>
              {t("lineProfile.useLinePhoto")}
            </Button>
          ) : null}
        </div>

        <div className="space-y-2 text-center">
          <p className="jod-kicker">{t("lineProfile.useGlyph")}</p>
          <div className="flex justify-center gap-2">
            {nicknameGlyphs.map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => {
                  setGlyph(g);
                  setCustomPhoto((prev) => {
                    if (prev) URL.revokeObjectURL(prev);
                    return null;
                  });
                }}
                className={`grid size-9 place-items-center rounded-full border text-lg transition-all ${
                  glyph === g ? "border-gold bg-[oklch(79%_0.12_80_/_12%)]" : "border-hairline"
                }`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("lineProfile.namePlaceholder")}
          className="text-center"
        />
        <p className="flex items-center justify-center gap-1.5 text-center text-cream-3 text-xs">
          <Icons.IconLock size={12} />
          {t("lineProfile.privacyNote")}
        </p>
      </div>

      <div className="space-y-3">
        <Button
          size="lg"
          className="w-full"
          disabled={saving}
          onClick={() => {
            void continueToJoin();
          }}
        >
          {saving ? t("common.saving") : t("lineProfile.useProfile")}
        </Button>
      </div>
    </div>
  );
}
